/**
 * User-facing activity log — shows a stacked list of status pills
 * indicating what each active worker is currently doing.
 * Positioned below the TaskProgress card (top-left).
 *
 * Supports multiple concurrent workers, one line per worker.
 * Each line auto-hides individually after its timeout.
 * NOT the dev log — this is minimal, clean, user-friendly.
 */

const ICON_MAP: Record<string, string> = {
  'Starting task': '🚀',
  'Calling AI': '🧠',
  'Refining results': '✨',
  'Generating title': '📝',
  'Storing deliverable': '💾',
  'Finished': '✅',
  'Working on': '⚡',
  'Task failed': '❌',
};

function getIcon(activity: string): string {
  for (const [key, icon] of Object.entries(ICON_MAP)) {
    if (activity.includes(key)) return icon;
  }
  return '🔍';
}

interface ActivityEntry {
  workerName: string;
  activity: string;
  el: HTMLElement;
  timer: ReturnType<typeof setTimeout>;
}

export class ActivityLog {
  private panel!: HTMLElement;
  private entries = new Map<string, ActivityEntry>(); // keyed by workerName

  constructor() {
    this.createPanel();
  }

  private createPanel(): void {
    this.panel = document.createElement('div');
    this.panel.id = 'activity-log';
    this.panel.style.cssText = `
      position: fixed;
      top: 80px;
      left: 24px;
      z-index: 12;
      display: flex;
      flex-direction: column;
      gap: 6px;
      pointer-events: none;
      max-width: 380px;
    `;
    document.body.appendChild(this.panel);
  }

  /**
   * Show activity status for a specific worker. Auto-hides after autoHideMs.
   * Multiple workers can be shown simultaneously — one row per worker.
   */
  show(workerName: string, activity: string, autoHideMs = 15000): void {
    // Dynamically position below task progress card if visible
    const cardEl = document.getElementById('task-progress-card');
    if (cardEl?.classList.contains('visible')) {
      const rect = cardEl.getBoundingClientRect();
      this.panel.style.top = `${rect.bottom + 12}px`;
    } else {
      this.panel.style.top = '80px';
    }

    const icon = getIcon(activity);
    const text = `${icon} ${workerName}: ${activity}`;

    const existing = this.entries.get(workerName);
    if (existing) {
      // Update existing row
      existing.activity = activity;
      existing.el.textContent = text;
      clearTimeout(existing.timer);
      existing.timer = setTimeout(() => this.removeEntry(workerName), autoHideMs);
      // Re-trigger fade-in if it was fading out
      existing.el.style.opacity = '1';
    } else {
      // Create new row
      const el = document.createElement('div');
      el.style.cssText = `
        background: rgba(15, 23, 42, 0.9);
        backdrop-filter: blur(8px);
        border-radius: 16px;
        padding: 8px 16px;
        color: #e2e8f0;
        font-family: system-ui, sans-serif;
        font-size: 13px;
        font-weight: 500;
        opacity: 0;
        transition: opacity 0.3s ease, transform 0.3s ease;
        transform: translateX(-10px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        border: 1px solid rgba(100, 116, 139, 0.3);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      `;
      el.textContent = text;
      this.panel.appendChild(el);

      // Animate in
      requestAnimationFrame(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateX(0)';
      });

      const timer = setTimeout(() => this.removeEntry(workerName), autoHideMs);
      this.entries.set(workerName, { workerName, activity, el, timer });
    }
  }

  private removeEntry(workerName: string): void {
    const entry = this.entries.get(workerName);
    if (!entry) return;

    clearTimeout(entry.timer);
    entry.el.style.opacity = '0';
    entry.el.style.transform = 'translateX(-10px)';

    // Remove from DOM after fade-out
    setTimeout(() => {
      entry.el.remove();
      this.entries.delete(workerName);
    }, 350);
  }

  hide(): void {
    for (const [name] of this.entries) {
      this.removeEntry(name);
    }
  }

  dispose(): void {
    this.hide();
    this.panel.remove();
  }
}
